import { CircularProgress, Stack, Typography } from '@mui/material';
import { uniq } from 'lodash';

import { CheckIcon } from '@/components/icons/CheckIcon';
import { COLORS } from '@/styles/tokens/colors';

import { HouseholdChatActivity } from '../contexts/householdChat.context';

const ACTIVITY_ICON_SIZE = 16;
// The spinner stroke fills its full box, so it reads larger than the check
// glyph at the same size. A smaller spinner keeps the two visually matched.
const ACTIVITY_SPINNER_SIZE = 14;

interface HouseholdChatActivityTraceProps {
  activities: HouseholdChatActivity[];
  /**
   * Whether the assistant is still working on this turn. While true the most
   * recent activity's row renders as in-progress (spinner); once false every
   * row renders as completed.
   */
  isStreaming: boolean;
}

/**
 * The live "show your work" trace rendered above the assistant's streamed
 * answer. Repeated activity labels collapse into a single row so a tool the
 * agent calls several times (e.g. "Searching the estate plan") shows once
 * instead of stacking identical rows.
 */
export function HouseholdChatActivityTrace({
  activities,
  isStreaming,
}: HouseholdChatActivityTraceProps) {
  if (activities.length === 0) {
    return null;
  }

  // Collapse repeated labels into one row each, keeping first-occurrence order.
  const labels = uniq(activities.map((activity) => activity.label));
  // The most recent activity drives the spinner; its label marks the live row.
  // The early return above guarantees there is one, so `at(-1)` is never undefined.
  const currentLabel = activities.at(-1)?.label;

  return (
    <Stack data-testid="household-chat-activity-trace" spacing={0.5}>
      {labels.map((label) => {
        const isCurrent = isStreaming && label === currentLabel;

        return (
          <Stack
            // Labels are unique after collapsing, so they are a stable row key.
            key={label}
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center' }}
          >
            {isCurrent ? (
              <CircularProgress
                size={ACTIVITY_SPINNER_SIZE}
                sx={{ color: COLORS.TEAL[600] }}
              />
            ) : (
              <CheckIcon
                size={ACTIVITY_ICON_SIZE}
                sx={{
                  color: COLORS.TEXT.SECONDARY,
                  // Center the inline SVG so it does not sit on the text baseline.
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              />
            )}
            <Typography
              variant="body-xs"
              sx={{
                color: isCurrent ? COLORS.TEAL[600] : COLORS.TEXT.SECONDARY,
                fontWeight: 500,
              }}
            >
              {label}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
}
